import React, { useEffect, useState } from "react";
import { Box, Paper, Grid, TextField, MenuItem, Button, Snackbar, Alert, Avatar } from "@mui/material";
import { useMasterData } from "../../../hooks/useMasterData";
import StudentHistoryFilters from "./StudentHistoryFilters";
import StudentTimeline from "./StudentTimeline";
import { studentTrackHistoryApi } from "./studentTrackHistoryApi";
import { certificateApi } from "../certificates/certificateApi";

const StudentTrackHistoryPage: React.FC = () => {
  const { masters, fetchMasters, fetchAcademicSessions, fetchSections } = useMasterData();
  const [studentQuery, setStudentQuery] = useState("");
  const [students, setStudents] = useState<any[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const [filters, setFilters] = useState<{ sessionId?: string; module?: string; dateFrom?: string; dateTo?: string }>({});
  const [timeline, setTimeline] = useState<any[]>([]);
  const [studentSummary, setStudentSummary] = useState<any>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({ open: false, message: "", severity: "info" });

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, []);

  const searchStudents = async () => {
    try {
      const res = await certificateApi.students({ search: studentQuery });
      setStudents(res);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to search students", severity: "error" });
    }
  };

  const loadHistory = async () => {
    if (!selectedStudentId) return;
    try {
      const res = await studentTrackHistoryApi.history(selectedStudentId, { sessionId: filters.sessionId, module: filters.module, dateFrom: filters.dateFrom, dateTo: filters.dateTo, page: 1, limit: 100 });
      setTimeline(res.items || []);
      setStudentSummary(res.student || null);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load history", severity: "error" });
    }
  };

  const resetFilters = () => {
    setFilters({});
    setTimeline([]);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ fontSize: 22, fontWeight: 600, mb: 2 }}>Student Track History</Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Search by Name/Admission/Scholar" value={studentQuery} onChange={(e) => setStudentQuery(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={searchStudents}>
              Search Student
            </Button>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth label="Select Student" value={selectedStudentId} onChange={(e) => setSelectedStudentId(e.target.value)}>
              {students.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.admissionNumber} — {s.firstName} {s.lastName} ({s.class?.name || ""} {s.section?.name || ""})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <StudentHistoryFilters sessions={masters.academicSessions} filters={filters} onChange={setFilters} onSearch={loadHistory} onReset={resetFilters} />
      </Paper>

      {studentSummary && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Avatar sx={{ width: 56, height: 56 }}>{(studentSummary.name || "?").charAt(0)}</Avatar>
            </Grid>
            <Grid item xs>
              <Box sx={{ fontWeight: 600 }}>{studentSummary.name}</Box>
              <Box>Adm No: {studentSummary.admissionNumber} • Scholar No: {studentSummary.scholarNumber || "-"}</Box>
              <Box>Class: {studentSummary.class || "-"} • Section: {studentSummary.section || "-"}</Box>
              <Box>House: {studentSummary.house || "-"} • Status: {studentSummary.status}</Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <StudentTimeline items={timeline} />
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentTrackHistoryPage;
